/**
 * RemoteConfigurationsTable Component
 *
 * Displays configurations in a sortable table with sticky headers.
 * Supports selection, sorting, and inline actions.
 */

var React = require('react');
var useState = React.useState;
var useMemo = React.useMemo;
var ConfigurationRow = require('./ConfigurationRow');
var { TABLE_COLUMNS, DEFAULT_SORT } = require('./RemoteConfigurations.types');
var { sortConfigurations } = require('./RemoteConfigurations.util');

/**
 * RemoteConfigurationsTable component
 * @param {Object} props
 * @param {Array} props.configurations - Array of configuration objects
 * @param {string|null} props.selectedConfigId - Currently selected configuration ID
 * @param {Function} props.onSelectConfig - Called when a row is selected
 * @param {Function} props.onToggle - Called when toggle is clicked
 * @param {Function} props.onEdit - Called when edit button is clicked
 * @param {Function} props.onDelete - Called when delete button is clicked
 */
var RemoteConfigurationsTable = function (props) {
  var configurations = props.configurations;
  var selectedConfigId = props.selectedConfigId;
  var onSelectConfig = props.onSelectConfig;
  var onToggle = props.onToggle;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;

  var sortConfigState = useState(DEFAULT_SORT);
  var sortConfig = sortConfigState[0];
  var setSortConfig = sortConfigState[1];

  // Sort configurations
  var sortedConfigurations = useMemo(
    function () {
      return sortConfigurations(configurations, sortConfig);
    },
    [configurations, sortConfig],
  );

  var handleSort = function (key) {
    setSortConfig(function (prev) {
      if (prev.key === key) {
        return {
          key: key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        key: key,
        direction: 'asc',
      };
    });
  };

  var renderSortIcon = function (columnKey, isSortable) {
    if (!isSortable) {
      return null;
    }
    if (sortConfig.key === columnKey) {
      return React.createElement(
        'span',
        {
          className:
            'remote-config-table__sort-icon remote-config-table__sort-icon--active',
        },
        sortConfig.direction === 'asc' ? ' ↑' : ' ↓',
      );
    }
    // Show subtle indicator for sortable columns
    return React.createElement(
      'span',
      {
        className:
          'remote-config-table__sort-icon remote-config-table__sort-icon--inactive',
      },
      ' ↕',
    );
  };

  var renderHeaderCell = function (column) {
    var classNames = 'remote-config-table__th';
    if (column.sortable) {
      classNames += ' remote-config-table__th--sortable';
    }

    var style = { width: column.width };
    if (column.minWidth) {
      style.minWidth = column.minWidth;
    }

    return React.createElement(
      'th',
      {
        key: column.key,
        className: classNames,
        style: style,
        onClick: column.sortable
          ? function () {
              handleSort(column.key);
            }
          : undefined,
      },
      [
        React.createElement('span', { key: 'label' }, column.label),
        renderSortIcon(column.key, column.sortable),
      ],
    );
  };

  // Empty state
  if (!configurations || configurations.length === 0) {
    return React.createElement(
      'div',
      {
        className: 'remote-config-table-container',
      },
      React.createElement(
        'div',
        {
          className: 'remote-config-empty',
        },
        React.createElement('p', null, 'No configurations found'),
      ),
    );
  }

  return React.createElement(
    'div',
    {
      className: 'remote-config-table-container',
    },
    React.createElement(
      'table',
      {
        className: 'remote-config-table',
      },
      [
        // Table header
        React.createElement(
          'thead',
          { key: 'thead' },
          React.createElement('tr', null, TABLE_COLUMNS.map(renderHeaderCell)),
        ),
        // Table body
        React.createElement(
          'tbody',
          { key: 'tbody' },
          sortedConfigurations.map(function (config) {
            return React.createElement(ConfigurationRow, {
              key: config.id,
              configuration: config,
              isSelected: selectedConfigId === config.id,
              onSelect: onSelectConfig,
              onToggle: onToggle,
              onEdit: onEdit,
              onDelete: onDelete,
            });
          }),
        ),
      ],
    ),
  );
};

module.exports = RemoteConfigurationsTable;
